import { useEffect, useRef } from "react";
import { usePlayer } from "../store/player";
import { addArtistUsage } from "../lib/artistUsage";
import { api } from "../lib/api";

export function GlobalAudio() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const lastPositionRef = useRef<number>(0);
  const accumulatedBytesRef = useRef<number>(0);
  const currentArtistRef = useRef<{ id: string | number | null; name: string } | null>(null);

  const {
    current,
    playing,
    repeatMode,
    volume,
    seekTarget,
    next,
    previous,
    clearSeekTarget,
  } = usePlayer();

  const flushUsage = () => {
    if (accumulatedBytesRef.current > 0 && currentArtistRef.current) {
      addArtistUsage(
        currentArtistRef.current.id,
        currentArtistRef.current.name,
        accumulatedBytesRef.current
      );
      accumulatedBytesRef.current = 0;
    }
  };

  // Sync current artist ref and flush previous track usage when track changes
  useEffect(() => {
    flushUsage();
    lastPositionRef.current = 0;
    if (current) {
      const artistId = (current as any)?.artist_id || (current as any)?.artist?.id || null;
      const artistName = current.artist?.name || (current as any)?.artist_name || (typeof current.artist === "string" ? current.artist : "Unknown Artist");
      currentArtistRef.current = { id: artistId, name: artistName };
    } else {
      currentArtistRef.current = null;
    }
  }, [current]);

  // Flush usage when pausing or unmounting
  useEffect(() => {
    if (!playing) {
      flushUsage();
    }
    return () => {
      flushUsage();
    };
  }, [playing]);

  // Sync HTML5 audio with player store state
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !current?.audio_url) return;

    if (playing) {
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => usePlayer.setState({ playing: false }));
      }
    } else {
      audio.pause();
    }
  }, [current, playing]);

  // Handle Seek Target from player store
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || seekTarget === null) return;
    try {
      audio.currentTime = seekTarget;
      lastPositionRef.current = seekTarget;
    } catch (e) {
      console.error("Seek error", e);
    }
    clearSeekTarget();
  }, [seekTarget, clearSeekTarget]);

  // Handle Volume
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.volume = volume;
  }, [volume]);

  // Sync MediaSession metadata and controls
  useEffect(() => {
    if (!("mediaSession" in navigator)) return;

    const setHandler = (action: MediaSessionAction, handler: MediaSessionActionHandler | null) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {}
    };

    setHandler("play", () => usePlayer.setState({ playing: true }));
    setHandler("pause", () => usePlayer.setState({ playing: false }));
    setHandler("nexttrack", () => usePlayer.getState().next());
    setHandler("previoustrack", () => usePlayer.getState().previous());
    setHandler("seekto", (details) => {
      if (typeof details.seekTime === "number") {
        usePlayer.getState().seek(details.seekTime);
      }
    });

    // Explicitly disable seek forward / seek backward to prevent iOS Safari from displaying skip 10s/15s buttons instead of next/previous track buttons
    setHandler("seekforward", null);
    setHandler("seekbackward", null);

    return () => {
      setHandler("play", null);
      setHandler("pause", null);
      setHandler("nexttrack", null);
      setHandler("previoustrack", null);
      setHandler("seekto", null);
      setHandler("seekforward", null);
      setHandler("seekbackward", null);
    };
  }, []);

  useEffect(() => {
    if (!current || !("mediaSession" in navigator) || typeof MediaMetadata === "undefined") return;
    const title = current.title?.trim() || "Music Powered by Yours";
    const rawArtist = current.artist as any;
    const artistName =
      (typeof rawArtist === "object" && rawArtist?.name?.trim()) ||
      (current as any)?.artist_name?.trim() ||
      (typeof rawArtist === "string" ? rawArtist.trim() : "") ||
      "Unknown Artist";
    const artworkSrc =
      (current.thumbnail && current.thumbnail.trim()) ||
      (current.artwork && current.artwork.trim()) ||
      "/fallback-media-cover.jpg";

    navigator.mediaSession.metadata = new MediaMetadata({
      title,
      artist: artistName,
      artwork: [{ src: artworkSrc }],
    });
  }, [current]);

  const handleEnded = () => {
    flushUsage();
    const audio = audioRef.current;
    if (repeatMode === "one" && audio) {
      audio.currentTime = 0;
      lastPositionRef.current = 0;
      if (current?.id) {
        api.recordSongPlay(current.id).catch(() => { });
      }
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === "function") {
        playPromise.catch(() => usePlayer.setState({ playing: false }));
      }
      return;
    }
    next();
  };

  const updatePositionState = () => {
    if (!("mediaSession" in navigator) || typeof navigator.mediaSession.setPositionState !== "function") return;
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    try {
      navigator.mediaSession.setPositionState({
        duration: audio.duration,
        playbackRate: audio.playbackRate || 1.0,
        position: Math.min(Math.max(0, audio.currentTime), audio.duration),
      });
    } catch {}
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    // Track artist streaming bandwidth
    const delta = audio.currentTime - lastPositionRef.current;
    if (delta > 0 && delta < 5 && currentArtistRef.current) {
      const bitrate = Number((current as any)?.bitrate ?? 128);
      const bytes = Math.round(bitrate * 125 * delta);
      accumulatedBytesRef.current += bytes;

      // Flush in batches to local storage every 32 KB
      if (accumulatedBytesRef.current > 32 * 1024) {
        flushUsage();
      }
    }
    lastPositionRef.current = audio.currentTime;

    usePlayer.setState({
      currentTime: audio.currentTime,
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });

    updatePositionState();
  };

  const handleLoadedMetadata = () => {
    const audio = audioRef.current;
    if (!audio) return;
    usePlayer.setState({
      duration: Number.isFinite(audio.duration) ? audio.duration : 0,
    });
    updatePositionState();
  };

  return (
    <audio
      ref={audioRef}
      src={current?.audio_url || undefined}
      onTimeUpdate={handleTimeUpdate}
      onLoadedMetadata={handleLoadedMetadata}
      onDurationChange={handleLoadedMetadata}
      onEnded={handleEnded}
      onWaiting={() => usePlayer.setState({ isBuffering: true })}
      onPlaying={() => usePlayer.setState({ isBuffering: false })}
      onCanPlay={() => usePlayer.setState({ isBuffering: false })}
      onError={() => usePlayer.setState({ isBuffering: false, playing: false })}
    />
  );
}
